import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getDatabase, ref, set, update } from 'firebase/database'
import fbConf from '../../FirebaseConf'
import Icon from '../../Icon'
import Switch from '../../components/Switch'
import Button from '../../components/Button'
import { useNotification } from '../../modules/Notification'
import { UserTabProps } from './types'
import { PushNotificationKey } from '../../types/VoltieUser'
import {
  Card, CardTitle, DetailLabel, Badge, GroupLabel, ToggleRow, ToggleLabel,
  CalcGrid, CalcRow, CalcInput, CalcUnit, SaveRow, LangGrid, LangOption, RecipientRow, RecipientInput, IconButton,
} from './userStyles'
import { normalizeCurrency, additionalEmails } from './userHelpers'
import UserChargerPushSettings from './UserChargerPushSettings'
import { LANGUAGES, flagIcon } from '../../languages'
import { Money } from '../../types/VoltieUser'

const PUSH_GROUPS: { titleKey: string; keys: PushNotificationKey[] }[] = [
  { titleKey: 'user_pushGroupCharger', keys: ['car_plugged_in', 'car_unplugged', 'charger_not_available', 'charger_errors', 'charger_software_update'] },
  { titleKey: 'user_pushGroupCharging', keys: ['charging_started', 'charging_finished', 'scheduled_charging_started', 'limited_charging_started'] },
  { titleKey: 'user_pushGroupVehicle', keys: ['car_not_charging', 'car_battery_level_reached'] },
  { titleKey: 'user_pushGroupEmails', keys: ['statistics_email', 'newsletter'] },
]

const CALC_FIELDS: { key: 'price' | 'average_consumption' | 'co2saving' | 'petrol_price' | 'petrol_avg_consumption' | 'battery_size' | 'public_price'; labelKey: string; unit: string; money: boolean }[] = [
  { key: 'price', labelKey: 'user_calcEnergy', unit: '/ kWh', money: true },
  { key: 'average_consumption', labelKey: 'user_calcAvgConsumption', unit: 'kWh / 100 km', money: false },
  { key: 'co2saving', labelKey: 'user_calcCo2', unit: 'g / km', money: false },
  { key: 'petrol_price', labelKey: 'user_calcPetrolPrice', unit: '/ l', money: true },
  { key: 'petrol_avg_consumption', labelKey: 'user_calcPetrolAvg', unit: 'l / 100 km', money: false },
  { key: 'battery_size', labelKey: 'user_calcBatterySize', unit: 'kWh', money: false },
  { key: 'public_price', labelKey: 'user_calcPublicPrice', unit: '/ kWh', money: true },
]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type CalcDraft = { [key: string]: any }

// The profile modal shows one section per menu item (VLT-2637); without one, this is the full admin
// sheet. The statistics email recipients belong to the admin sheet only.
export type SettingsSection = 'language' | 'notifications' | 'calculations'

export default function UserNotifications({ user, uid, refresh, section }: UserTabProps & { section?: SettingsSection }) {
  const { t } = useTranslation()
  const { addNotification } = useNotification()
  const db = getDatabase(fbConf)

  const [pushState, setPushState] = useState<{ [k: string]: boolean }>({})
  const [calcDraft, setCalcDraft] = useState<CalcDraft>({})
  const [recipients, setRecipients] = useState<string[]>([])
  const [newEmail, setNewEmail] = useState<string>('')
  const [lang, setLang] = useState<string>('')

  // (Re)initialize editable state whenever the user node changes.
  useEffect(() => {
    const push = user.preferences?.push_notifications || {}
    const initPush: { [k: string]: boolean } = {}
    for (const g of PUSH_GROUPS) for (const k of g.keys) {
      // Read the legacy alias for the battery key when present.
      initPush[k] = k === 'car_battery_level_reached'
        ? !!(push.car_battery_level_reached ?? push.battery_level_reached)
        : !!push[k]
    }
    setPushState(initPush)

    const cv = user.preferences?.calculation_values || {}
    const draft: CalcDraft = {}
    for (const f of CALC_FIELDS) {
      const v = cv[f.key]
      if (f.money) draft[f.key] = { currency: (v as Money)?.currency || 'HUF', value: (v as Money)?.value ?? 0 }
      else draft[f.key] = (v as number) ?? 0
    }
    setCalcDraft(draft)

    setRecipients(additionalEmails(user.preferences?.additional_email))
    setLang(user.preferences?.language || '')
  }, [user])

  async function saveLanguage(code: string) {
    setLang(code)
    try {
      await set(ref(db, `voltie/chargerusers/${uid}/preferences/language`), code)
      addNotification(t('user_saved'), 'TEXT', 'normal', 1500)
      refresh()
    } catch (e) { addNotification(`${e}`, 'TEXT', 'error') }
  }

  async function savePush() {
    try {
      // Mirror the legacy aliases so older app/backend readers see the change too.
      const payload = { ...pushState, battery_level_reached: pushState.car_battery_level_reached }
      await update(ref(db, `voltie/chargerusers/${uid}/preferences/push_notifications`), payload)
      await set(ref(db, `voltie/chargerusers/${uid}/preferences/statistic_emails`), !!pushState.statistics_email)
      addNotification(t('user_saved'), 'TEXT', 'normal', 1500)
      refresh()
    } catch (e) { addNotification(`${e}`, 'TEXT', 'error') }
  }

  async function saveCalc() {
    try {
      // Only write fields the admin actually changed, so untouched/absent fields aren't materialized
      // with zero defaults; normalize legacy currency codes on the money fields we do write.
      const orig = user.preferences?.calculation_values || {}
      const payload: CalcDraft = {}
      for (const f of CALC_FIELDS) {
        const draft = calcDraft[f.key]
        if (f.money) {
          const o = orig[f.key] as Money | undefined
          if (!o || o.value !== draft.value || normalizeCurrency(o.currency) !== normalizeCurrency(draft.currency)) {
            payload[f.key] = { currency: normalizeCurrency(draft.currency) || 'HUF', value: draft.value }
          }
        } else if (orig[f.key] !== draft) {
          payload[f.key] = draft
        }
      }
      if (Object.keys(payload).length === 0) { addNotification(t('user_saved'), 'TEXT', 'normal', 1500); return }
      await update(ref(db, `voltie/chargerusers/${uid}/preferences/calculation_values`), payload)
      addNotification(t('user_saved'), 'TEXT', 'normal', 1500)
      refresh()
    } catch (e) { addNotification(`${e}`, 'TEXT', 'error') }
  }

  async function saveRecipients(list: string[]) {
    try {
      await set(ref(db, `voltie/chargerusers/${uid}/preferences/additional_email`), list)
      setRecipients(list)
      refresh()
    } catch (e) { addNotification(`${e}`, 'TEXT', 'error') }
  }

  function addRecipient() {
    const email = newEmail.trim().toLowerCase()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { addNotification(t('user_invalidEmail'), 'TEXT', 'warning'); return }
    if (email === (user.email || '').toLowerCase() || recipients.includes(email)) { setNewEmail(''); return }
    saveRecipients([...recipients, email])
    setNewEmail('')
  }

  const isShown = (candidate: SettingsSection) => !section || section === candidate

  const resetPush = () => {
    const push = user.preferences?.push_notifications || {}
    const init: { [k: string]: boolean } = {}
    for (const g of PUSH_GROUPS) for (const k of g.keys) init[k] = k === 'car_battery_level_reached' ? !!(push.car_battery_level_reached ?? push.battery_level_reached) : !!push[k]
    setPushState(init)
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-start' }}>
      {/* Two equal columns, each half the content width, top-aligned */}
      {(isShown('language') || isShown('notifications')) && <div style={{ flex: '1 1 360px', minWidth: 0 }}>
      {/* Language */}
      {isShown('language') && <Card>
        <CardTitle>{t('user_languageTitle')}</CardTitle>
        <LangGrid role="radiogroup" aria-label={t('user_languageTitle')}>
          {LANGUAGES.map(({ code, label }) => (
            <LangOption key={code} role="radio" aria-checked={lang === code} tabIndex={0} $selected={lang === code}
              onClick={() => saveLanguage(code)}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); saveLanguage(code) } }}>
              <Icon path={flagIcon(code)} width="18px" height="18px" />{label}
            </LangOption>
          ))}
        </LangGrid>
      </Card>}

      {/* Push notifications */}
      {isShown('notifications') && <Card>
        <CardTitle>{t('user_pushTitle')}</CardTitle>
        {PUSH_GROUPS.map(group => (
          <div key={group.titleKey}>
            <GroupLabel>{t(group.titleKey)}</GroupLabel>
            {group.keys.map(k => (
              <ToggleRow key={k}>
                <ToggleLabel>{t(`user_push_${k}`)}</ToggleLabel>
                <Switch ariaLabel={t(`user_push_${k}`)} toggled={!!pushState[k]} setToggled={(v) => setPushState(prev => ({ ...prev, [k]: v }))} />
              </ToggleRow>
            ))}
          </div>
        ))}
        <SaveRow>
          <Button label={t('user_cancel')} width="100px" onclick={resetPush} bgcolor="transparent" labelcolor="black" />
          <Button label={t('user_save')} width="100px" onclick={savePush} />
        </SaveRow>
      </Card>}
      </div>}

      {isShown('calculations') && <div style={{ flex: '1 1 360px', minWidth: 0 }}>
      {/* Statistics email recipients + calculation values */}
      {!section && <Card>
        <CardTitle>{t('user_statEmailTitle')}</CardTitle>
        {user.email && <RecipientRow><Badge $tone="neutral">{user.email}</Badge></RecipientRow>}
        {recipients.map(email => (
          <RecipientRow key={email}>
            <span style={{ flex: 1, fontSize: '14px' }}>{email}</span>
            <IconButton aria-label={t('user_removeRecipient')} onClick={() => saveRecipients(recipients.filter(e => e !== email))}>
              <Icon path="general.x" width="14px" height="14px" color="danger" />
            </IconButton>
          </RecipientRow>
        ))}
        <RecipientRow>
          <RecipientInput aria-label={t('user_addEmailPlaceholder')} value={newEmail} placeholder={t('user_addEmailPlaceholder')} onChange={e => setNewEmail(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addRecipient() }} />
          <Button label={t('user_add')} width="120px" onclick={addRecipient} />
        </RecipientRow>
      </Card>}

      {/* Calculation values */}
      <Card>
        <CardTitle>{t('user_calcTitle')}</CardTitle>
        <CalcGrid>
          {CALC_FIELDS.map(f => {
            const draft = calcDraft[f.key]
            const value = f.money ? draft?.value : draft
            const unit = f.money ? `${normalizeCurrency(draft?.currency)} ${f.unit}` : f.unit
            return (
              <CalcRow key={f.key}>
                <DetailLabel style={{ margin: 0, textTransform: 'none', fontSize: '13px', letterSpacing: 0 }}>{t(f.labelKey)}</DetailLabel>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CalcInput type="number" aria-label={t(f.labelKey)} value={value ?? 0} onChange={e => {
                    const num = e.target.value === '' ? 0 : parseFloat(e.target.value)
                    setCalcDraft(prev => ({ ...prev, [f.key]: f.money ? { ...prev[f.key], value: num } : num }))
                  }} />
                  <CalcUnit>{unit}</CalcUnit>
                </div>
              </CalcRow>
            )
          })}
        </CalcGrid>
        {/* A raw enum, useful to an admin reading someone's sheet and noise in a user's own settings. */}
        {!section && <div style={{ marginTop: '16px' }}>
          <DetailLabel>{t('user_priceType')}</DetailLabel>
          <Badge $tone="neutral">{user.preferences?.calculation_values?.priceType || '-'}</Badge>
        </div>}
        <SaveRow>
          <Button label={t('user_save')} width="100px" onclick={saveCalc} />
        </SaveRow>
      </Card>
      </div>}

      {/* Admin sheet only: support reads it to answer why a charger did or did not notify (VLT-2554). */}
      {!section && <div style={{ flex: '1 1 100%', minWidth: 0 }}>
        <UserChargerPushSettings user={user} />
      </div>}
    </div>
  )
}
